import { Experiment, Organization, WorkflowNotificationTask, WorkflowNotificationTaskType, WorkflowsService } from '@/api/';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { useStore } from '@nanostores/react';
import {
  Loader2,
  Play,
  CheckCircle,
  XCircle
} from 'lucide-react';
import {
    Badge,
 } from '@/components/ui/badge';
 import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useState } from 'react';
import { Input } from '@/components/ui/input';

/**
 * We'll update the selected project in the store and route
 * to the project page.
 */
async function handleSelectTask(
  organization: Organization,
  taskId: string,
  paymentMethod: { id: string; name: string } | null,
  accountType: { id: string; name: string } | null,
  referenceNumber: string | null
) {
  var request = {
    paymentMethod: paymentMethod,
    accountType: accountType,
    referenceNumber: referenceNumber
  };
  var response = await updateTask(taskId, organization.id, request);
  if (response.success) {
    $tasks.set($tasks.get().filter(task => task.id !== taskId));
    toast.success('Task executed successfully');
  } else {
    const currentTask = $task.get();
    if (currentTask) {
      $task.set({
        ...currentTask,
        status: 'FAILED'
      });
    }
    toast.error('Task execution failed');
  }
}

export const TasksListing = () => {
  const org = useStore($org);

  const tasks = useStore($tasks);
  const loadingTasks = useStore($loadingTasks);

  const sortedTasks = tasks
    ?.slice()
    ?.sort(
      (a, b) =>
        new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime(),
    );

  const selectedTask = useStore($task);
  
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<{ id: string; name: string } | null>(null);
  const [selectedAccountType, setSelectedAccountType] = useState<{ id: string; name: string } | null>(null);
  const [referenceNumber, setReferenceNumber] = useState<string>('');
  const [loadingFileId, setLoadingFileId] = useState<string | null>(null);

  // Handle empty state when no account receivables exist
  if (!loadingTasks && (!tasks || tasks.length === 0)) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p>No tasks found.</p>
      </div>
    );
  }

  const formatDateString = (date: string | Date) => {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are zero-based, so add 1
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };


  function getDateRangeString(
    startDate: string | Date,
    endDate: string | Date,
  ) {
    const startDateString = formatDateString(startDate);

    const endDateString = formatDateString(endDate);

    if (startDateString === endDateString) {
      return `for ${startDateString}`;
    } else {
      return `between ${startDateString} and ${endDateString}`;
    }
  }

  const taskType = (task: WorkflowNotificationTask) => {
    if (task.type === 'EMAIL_NOTIFICATION') {
      return 'Remittance Invoice';
    }
    return 'Workflow';
  }

  const handleFileClick = async (e: React.MouseEvent, resourceId: string, taskId: string) => {
    e.preventDefault();
    setLoadingFileId(resourceId);
    
    try {
      // Generate a fresh presigned URL
      
      const response = await WorkflowsService.notificationTaskResourcePresignedUrl(org?.id || '', taskId, resourceId);
      
      // Open in new window
      window.open(response.presignedUrl, '_blank');
    } catch (error) {
      console.error('Error getting presigned URL:', error);
      toast.error('Failed to open file');
    } finally {
      setLoadingFileId(null);
    }
  };

  return (
    <>
      {loadingTasks ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <ul role="list" className="space-y-4">
          {sortedTasks?.map(task => {
            return (
              <li
                key={task.id}
                className="flex items-center justify-between gap-x-6 p-6 border rounded-xl bg-card text-card-foreground shadow-sm hover:shadow-md transition-all duration-200 ease-in-out"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-x-3">
                    <div className="flex flex-col gap-y-2">
                      <p className="text-lg font-semibold leading-6">
                        {task.name}
                        <br/>
                        {taskType(task)}
                      </p>
                      <div className="flex">
                        <Badge variant={task.data?.status === 'Posted' ? 'default' : 'secondary'}>
                          {task.data?.status}
                        </Badge>
                        {task.data?.remittance_type && (
                          <Badge variant="outline" className="text-xs">
                            {task.data?.remittance_type}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* New section for task details */}
                  <div className="mt-1 flex flex-col gap-y-1 text-sm leading-5 text-muted-foreground">
                    <div className="flex flex-col gap-y-1">
                      <div className="flex items-center gap-x-2">
                        <p className="whitespace-nowrap font-medium">
                          Payee: {task.data?.payee?.name}
                        </p>
                        <Badge variant="outline" className="text-xs">
                          {task.data?.payee?.bank_name}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          BSB: {task.data?.payee?.bsb_number}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          ACC: {task.data?.payee?.account_number}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-x-2">
                        <p className="whitespace-nowrap">
                          Address: {task.data?.payee?.address}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-x-2">
                      <p className="whitespace-nowrap font-medium">
                        Payment Amount: ${task.data?.payment?.amount?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </p>
                      <Badge variant="secondary" className="text-xs">
                        {new Date(task.data?.payment?.date ?? new Date()).toLocaleDateString()}
                      </Badge>
                    </div>

                    <div className="flex flex-col gap-y-1">
                      <p className="font-medium">Invoice Details:</p>
                      {task.data?.payment?.details?.map((detail, index) => (
                        <div key={index} className="flex items-center gap-x-2 pl-2">
                          <Badge variant="outline" className="text-xs">
                            #{detail.invoice_number}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {new Date(detail.invoice_date).toLocaleDateString()}
                          </Badge>
                          <p className="whitespace-nowrap">
                            ${detail.payment_amount.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div>
                      <p className="mb-1 text-sm text-muted-foreground">Payment Type</p>
                      <Select
                        onValueChange={(value) => {
                          const selectedMethod = task?.data?.quickbooks_account_metadata?.payment_methods?.find(
                            m => m.id === value
                          );
                          if (selectedMethod) {
                            setSelectedPaymentMethod({ id: selectedMethod.id, name: selectedMethod.name });
                          }
                        }}
                        value={selectedPaymentMethod?.id ?? ''}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Select payment type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectLabel>Payment Methods</SelectLabel>
                            {task?.data?.quickbooks_account_metadata?.payment_methods?.map((method, index) => (
                              <SelectItem key={index} value={method.id}>
                                {method.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>  
                      </Select>
                    </div>

                    <div>
                      <p className="mb-1 text-sm text-muted-foreground">Deposit To</p>
                      <Select
                        onValueChange={(value) => {
                          const selectedType = task?.data?.quickbooks_account_metadata?.account_types?.find(
                            t => t.id === value
                          );
                          if (selectedType) {
                            setSelectedAccountType({ id: selectedType.id, name: selectedType.name });
                          }
                        }}
                        value={selectedAccountType?.id ?? ''}
                      >
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Select account type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectLabel>Account Reference</SelectLabel>
                            {task?.data?.quickbooks_account_metadata?.account_types?.map((type, index) => (
                              <SelectItem key={index} value={type.id}>
                                {type.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <p className="mb-1 text-sm text-muted-foreground">Reference Number</p>
                      <Input
                        placeholder="Enter reference number"
                        value={referenceNumber}
                        onChange={(e) => setReferenceNumber(e.target.value)}
                        className="w-[180px]"
                      />
                    </div>
                  </div>

                  

                  <div className="mt-1 flex items-center gap-x-2 text-xs leading-5 text-muted-foreground">
                    <p className="whitespace-nowrap">
                      Created on{' '}
                      <time dateTime={task.createdAtUtc}>
                        {new Date(task.createdAtUtc).toLocaleDateString()}
                      </time>
                    </p>
                  </div>
                </div>

                {task.workflowNotificationTaskResources && task.workflowNotificationTaskResources.length > 0 && (
                <div className="mt-4 flex flex-col gap-2">
                  <p className="text-sm font-medium text-gray-600">Files:</p>
                  <div className="flex flex-wrap gap-2">
                    {task.workflowNotificationTaskResources.map((resource, index) => (
                      <a 
                        key={index}
                        href="#"
                        onClick={(e) => handleFileClick(e, resource.id, task.id)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        {loadingFileId === resource.id ? (
                          <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading...
                          </span>
                        ) : (
                          resource.fileName
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              )}

                <div className="flex flex-none items-center gap-x-4">
                  <Button
                    onClick={async e => {
                      if (org && task) await handleSelectTask(org, task.id, selectedPaymentMethod, selectedAccountType, referenceNumber);
                    }}
                    className="flex items-center gap-2"
                  >
                    Execute Task
                    <span className="sr-only">, {task.name}</span>
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
};
